import { useCallback, useRef, useState } from 'react';
import { recipeV2Client, RecipeV2ClientMocked } from '../../../app/services/api/recipeV2';
import { RecipeBankAllergenFilter } from '../../../app/services/api/recipeV2/recipeBankAllergenFilter';
import { isDemoModeEnabled } from '../../../utils/featureAccess';
import { RecipeV2 } from '../types';

let demoClient: RecipeV2ClientMocked | undefined;

/**
 * Recipe Bank is a read-only catalogue of Active Recipe V2 records. Governed mode queries
 * the bank endpoint; Demo Mode uses the Thomas Franks catalogue with the same page/count shape.
 */
const useRecipeBankList = () => {
  const [recipes, setRecipes] = useState<RecipeV2[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const loadRecipes = useCallback(async (
    search: string,
    category: string,
    dietaryAttributeIds: number[],
    page: number,
    pageSize: number,
    allergenFilter?: RecipeBankAllergenFilter,
  ) => {
    const currentRequestId = ++requestId.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = isDemoModeEnabled()
        ? await (demoClient ??= new RecipeV2ClientMocked()).getRecipeBankList(
          search, category, dietaryAttributeIds, page + 1, pageSize, allergenFilter,
        )
        : await recipeV2Client.getRecipeBankList(
          search, category, dietaryAttributeIds, page + 1, pageSize,
        );
      if (currentRequestId !== requestId.current) return;
      setRecipes(response.data);
      setTotalCount(response.totalCount);
    } catch (err) {
      if (currentRequestId !== requestId.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load the recipe bank');
      setRecipes([]);
      setTotalCount(0);
    } finally {
      if (currentRequestId === requestId.current) setIsLoading(false);
    }
  }, []);

  return { recipes, totalCount, isLoading, error, loadRecipes };
};

export default useRecipeBankList;
